import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

export const LanguageSwitcher = ({ dark = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleClickOutside = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  }, [setOpen, ref]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        data-testid="language-switcher-trigger"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
          dark
            ? 'text-slate-700 hover:bg-slate-100'
            : 'text-white/90 hover:bg-white/10'
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{current.flag}</span>
        <span className="hidden md:inline uppercase tracking-wider text-xs">
          {current.code}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              data-testid={`language-option-${lang.code}`}
              onClick={() => change(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors ${
                current.code === lang.code ? 'bg-amber-50' : ''
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="flex-1 text-slate-800">{lang.label}</span>
              {current.code === lang.code && (
                <Check className="w-4 h-4 text-amber-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
